import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { crmIntegrationChatApi } from '../api/crm-integration-chat.api'
import {
  getCrmIntegrationApiErrorText,
  getCrmIntegrationSendErrorMessage,
} from '../lib/crmIntegrationApiErrors'
import { sortCrmIntegrationMessagesAsc } from '../lib/crmIntegrationMessagesMerge'
import {
  isServerMessageHiddenByPending,
  mapServerMessageToDisplay,
  pendingToDisplayMessage,
  prunePendingAgainstServer,
} from '../lib/crmIntegrationPendingMessages'
import type { CrmIntegrationMessageDisplay } from '../types/crm-integration-message.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import { isError } from '@/helpers/checkQuery'
import { useCrmIntegrationBrowserTabVisibleMessagesRefetch } from './useCrmIntegrationBrowserTabVisibleMessagesRefetch'
import { useCrmIntegrationLoadOlderMessages } from './useCrmIntegrationLoadOlderMessages'
import { useCrmIntegrationMarkRead } from './useCrmIntegrationMarkRead'
import {
  useCrmIntegrationMessagesLoad,
  type CrmIntegrationMessagesLoadStatus,
} from './useCrmIntegrationMessagesLoad'
import { useCrmIntegrationRealtimeMessages } from './useCrmIntegrationRealtimeMessages'
import { useCrmIntegrationSendMessage } from './useCrmIntegrationSendMessage'
import { useIntegrationChannelDeletedRealtime } from './useIntegrationChannelDeletedRealtime'

export type UseCrmIntegrationChatDataOptions = {
  provider: CrmChatProviderKey
  conversationId?: number | null
  enabled?: boolean
  scrollContainerRef: MutableRefObject<HTMLDivElement | null>
  onMarkReadSuccess?: (leadId: number, conversationId?: number) => void
  /**
   * Канал conversation удалён (WS integration_channel_deleted).
   * Вызывающий закрывает UI (назад к списку и т.п.).
   */
  onChannelDeleted?: () => void
}

export type UseCrmIntegrationChatDataResult = {
  status: CrmIntegrationMessagesLoadStatus
  conversationTitle: string
  unreadCount: number
  /** Wazzup: тип канала (whatsapp / telegram / instagram / …). */
  externalChatType: string | null
  messages: CrmIntegrationMessageDisplay[]
  errorMessage: string | null
  isSending: boolean
  sendMessage: (text: string, file?: File | null) => Promise<void>
  sendVoiceMessage: (
    file: File,
    previewUrl: string,
    durationSec?: number | null,
  ) => Promise<void>
  retrySendMessage: (clientId: string) => Promise<void>
  loadOlderMessages: () => Promise<void>
  isLoadingOlder: boolean
  hasMoreOlder: boolean
  markDialogRead: () => Promise<void>
  isMarkingRead: boolean
  hasConversation: boolean
  loadMessages: (loadOptions?: { silent?: boolean }) => Promise<void>
  deleteMessage: (messageId: number) => Promise<boolean>
  isDeletingMessageId: number | null
}

const getConversationTitle = (
  title: string | null | undefined,
  recipient: string | null | undefined,
  leadId: number,
): string => {
  const normalizedTitle = title?.trim()
  if (normalizedTitle) {
    return normalizedTitle
  }
  const normalizedRecipient = recipient?.trim()
  if (normalizedRecipient) {
    return normalizedRecipient
  }
  return `Лид #${leadId}`
}

export const useCrmIntegrationChatData = (
  leadId: number,
  options: UseCrmIntegrationChatDataOptions,
): UseCrmIntegrationChatDataResult => {
  const enabled = options.enabled !== false
  const conversationId =
    options.conversationId != null && Number.isFinite(Number(options.conversationId))
      ? Number(options.conversationId)
      : null

  const addAlert = useAddAlert()
  const [isDeletingMessageId, setIsDeletingMessageId] = useState<number | null>(
    null,
  )

  const scrollContainerRef = options.scrollContainerRef
  const markInitialPageLoadedRef = useRef<(messageCount: number) => void>(() => {})

  const profile = useSelector(
    (state: { profile?: { profile?: { id?: number | string } } }) =>
      state.profile?.profile,
  )
  const managerUserId =
    profile?.id != null && Number.isFinite(Number(profile.id))
      ? Number(profile.id)
      : undefined

  const {
    status,
    conversation,
    setConversation,
    serverMessages,
    setServerMessages,
    errorMessage,
    loadMessages,
  } = useCrmIntegrationMessagesLoad({
    conversationId,
    enabled,
    onServerMessagesUpdated: (_next, context) => {
      if (!context.silent) {
        markInitialPageLoadedRef.current(context.initialPageMessageCount)
      }
    },
  })

  const resetLocalUnreadCount = useCallback(() => {
    setConversation((prev) => (prev ? { ...prev, unread_count: 0 } : prev))
  }, [setConversation])

  const { markDialogRead, isMarkingRead } = useCrmIntegrationMarkRead({
    resetLocalUnreadCount,
    onMarkReadSuccess: options.onMarkReadSuccess,
  })

  const {
    pendingMessages,
    setPendingMessages,
    isSending,
    sendMessage,
    sendVoiceMessage,
    retrySendMessage,
    resetSendState,
    confirmOutgoingDelivery,
    handleOutgoingDeliveryFailed,
  } = useCrmIntegrationSendMessage({
    conversationId,
    provider: options.provider,
    managerUserId,
    loadMessages,
    wazzupChannelType: conversation?.external_chat_type ?? null,
  })

  const {
    loadOlderMessages,
    isLoadingOlder,
    hasMoreOlder,
    resetOlderPagination,
    markInitialPageLoaded,
  } = useCrmIntegrationLoadOlderMessages({
    conversationId,
    setServerMessages,
    scrollContainerRef,
  })

  markInitialPageLoadedRef.current = markInitialPageLoaded

  const patchServerMessageDelivery = useCallback(
    (
      messageId: number,
      patch: {
        status: 'sent' | 'failed' | 'pending'
        error?: string | null
        deliveredAt?: string | null
      },
    ) => {
      setServerMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                delivery: {
                  ...message.delivery,
                  status: patch.status,
                  error: patch.error ?? message.delivery.error,
                  deliveredAt:
                    patch.deliveredAt !== undefined
                      ? patch.deliveredAt
                      : message.delivery.deliveredAt,
                },
              }
            : message,
        ),
      )
    },
    [setServerMessages],
  )

  const handleOutgoingMessageDelivered = useCallback(
    (messageId: number) => {
      patchServerMessageDelivery(messageId, {
        status: 'sent',
        error: null,
      })
      confirmOutgoingDelivery(messageId)
    },
    [confirmOutgoingDelivery, patchServerMessageDelivery],
  )

  const handleOutgoingMessageNotDelivered = useCallback(
    ({ messageId, error }: { messageId: number; error: string }) => {
      patchServerMessageDelivery(messageId, {
        status: 'failed',
        error,
      })
      handleOutgoingDeliveryFailed(messageId, error)
    },
    [handleOutgoingDeliveryFailed, patchServerMessageDelivery],
  )

  const applyMessageDeletedLocally = useCallback(
    (payload: {
      messageId: number
      lastMessageId: string | null
      lastMessageText: string | null
      unreadCount?: number
    }) => {
      setServerMessages((prev) =>
        prev.filter((message) => message.id !== payload.messageId),
      )
      setConversation((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          last_message_id: payload.lastMessageId,
          last_message_text: payload.lastMessageText,
          unread_count:
            payload.unreadCount != null
              ? payload.unreadCount
              : prev.unread_count,
        }
      })
    },
    [setConversation, setServerMessages],
  )

  const resolveLocalLastMessageAfterDelete = useCallback(
    (
      deletedMessageId: number,
      currentLastMessageId: string | null | undefined,
    ) => {
      const wasLast =
        currentLastMessageId != null &&
        String(currentLastMessageId) === String(deletedMessageId)

      if (!wasLast) {
        return null
      }

      const remaining = serverMessages.filter(
        (message) => message.id !== deletedMessageId,
      )
      if (remaining.length === 0) {
        return {
          lastMessageId: null as string | null,
          lastMessageText: null as string | null,
        }
      }
      const last = remaining[remaining.length - 1]
      return {
        lastMessageId: String(last.id),
        lastMessageText: last.text?.trim() ? last.text : null,
      }
    },
    [serverMessages],
  )

  const deleteMessage = useCallback(
    async (messageId: number): Promise<boolean> => {
      if (!Number.isFinite(messageId) || messageId <= 0) {
        return false
      }
      if (isDeletingMessageId != null) {
        return false
      }

      try {
        setIsDeletingMessageId(messageId)
        const res = await crmIntegrationChatApi.deleteIntegrationMessage(
          messageId,
        )
        if (isError(res)) {
          throw new Error(
            getCrmIntegrationApiErrorText(
              res,
              'Не удалось удалить сообщение',
            ),
          )
        }

        const data = res?.data?.data
        const deleted =
          data?.deleted === true || Number(data?.message_id) === messageId
        if (!deleted) {
          throw new Error('Не удалось удалить сообщение')
        }

        const localLast = resolveLocalLastMessageAfterDelete(
          messageId,
          conversation?.last_message_id,
        )
        setServerMessages((prev) =>
          prev.filter((message) => message.id !== messageId),
        )
        if (localLast) {
          setConversation((prev) =>
            prev
              ? {
                  ...prev,
                  last_message_id: localLast.lastMessageId,
                  last_message_text: localLast.lastMessageText,
                }
              : prev,
          )
        }
        return true
      } catch (error) {
        addAlert({
          type: 'error',
          title: 'Не удалось удалить',
          text: getCrmIntegrationSendErrorMessage(error),
        })
        return false
      } finally {
        setIsDeletingMessageId(null)
      }
    },
    [
      addAlert,
      conversation?.last_message_id,
      isDeletingMessageId,
      resolveLocalLastMessageAfterDelete,
      setConversation,
      setServerMessages,
    ],
  )

  const handleRealtimeMessageDeleted = useCallback(
    (payload: {
      messageId: number
      conversationId: number
      lastMessageId: string | null
      lastMessageText: string | null
      unreadCount: number
    }) => {
      applyMessageDeletedLocally({
        messageId: payload.messageId,
        lastMessageId: payload.lastMessageId,
        lastMessageText: payload.lastMessageText,
        unreadCount: payload.unreadCount,
      })
    },
    [applyMessageDeletedLocally],
  )

  useCrmIntegrationRealtimeMessages({
    leadId,
    conversationId,
    provider: options.provider,
    enabled,
    loadMessages,
    onConversationRead: resetLocalUnreadCount,
    onOutgoingMessageDelivered: handleOutgoingMessageDelivered,
    onOutgoingMessageNotDelivered: handleOutgoingMessageNotDelivered,
    onMessageDeleted: handleRealtimeMessageDeleted,
  })

  const onChannelDeletedRef = useRef(options.onChannelDeleted)
  onChannelDeletedRef.current = options.onChannelDeleted

  useIntegrationChannelDeletedRealtime({
    enabled:
      enabled &&
      options.provider === 'wazzup' &&
      options.onChannelDeleted != null &&
      conversation != null,
    integrationId: conversation?.integration_id ?? null,
    externalChannelId: conversation?.external_channel_id ?? null,
    onDeleted: (payload) => {
      const channelName = String(payload.channel_name ?? '').trim()
      addAlert({
        title: 'Канал удалён',
        text: channelName
          ? `Канал «${channelName}» больше недоступен`
          : 'Канал Wazzup удалён и больше недоступен',
        type: 'warning',
      })
      onChannelDeletedRef.current?.()
    },
  })

  useCrmIntegrationBrowserTabVisibleMessagesRefetch({
    enabled,
    loadMessages,
  })

  const displayMessages = useMemo((): CrmIntegrationMessageDisplay[] => {
    const serverViews = serverMessages
      .filter(
        (message) => !isServerMessageHiddenByPending(message, pendingMessages),
      )
      .map(mapServerMessageToDisplay)
    const pendingViews = pendingMessages.map(pendingToDisplayMessage)
    return sortCrmIntegrationMessagesAsc([...serverViews, ...pendingViews])
  }, [pendingMessages, serverMessages])

  useEffect(() => {
    if (!enabled) {
      resetSendState()
      resetOlderPagination()
    }
  }, [enabled, resetOlderPagination, resetSendState])

  useLayoutEffect(() => {
    if (serverMessages.length === 0) {
      return
    }

    setPendingMessages((prev) => {
      if (prev.length === 0) {
        return prev
      }

      const next = prunePendingAgainstServer(serverMessages, prev)
      return next.length === prev.length ? prev : next
    })
  }, [serverMessages, setPendingMessages])

  const markAllAsRead = useCallback(
    () => markDialogRead(leadId, conversationId, conversation?.unread_count ?? 0),
    [conversation?.unread_count, conversationId, leadId, markDialogRead],
  )

  return {
    status,
    conversationTitle: getConversationTitle(
      conversation?.title,
      conversation?.external_recipient,
      leadId,
    ),
    unreadCount: conversation?.unread_count ?? 0,
    externalChatType: conversation?.external_chat_type ?? null,
    messages: displayMessages,
    errorMessage,
    isSending,
    sendMessage,
    sendVoiceMessage,
    retrySendMessage,
    loadOlderMessages,
    isLoadingOlder,
    hasMoreOlder,
    markDialogRead: markAllAsRead,
    isMarkingRead,
    hasConversation: conversation != null,
    loadMessages,
    deleteMessage,
    isDeletingMessageId,
  }
}
